import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useLocationStore } from '@/store/location'
import { useAlertsQuery } from '@/lib/nws/hooks'
import type { NwsAlert } from '@/lib/nws/types'
import { pickActiveWarning, pickActiveWatch, type StormPhase } from '@/lib/storm/classify'
import { demoKindFromSearch, makeDemoAlert } from '@/lib/storm/demo'

export interface StormEscalation {
  saved: ReturnType<typeof useLocationStore.getState>['saved']
  /** All active alerts (live + any ?demo= injection), most-severe first. */
  alerts: NwsAlert[]
  /** Highest-priority active warning → drives STORM MODE. Null otherwise. */
  warning: NwsAlert | null
  /** Highest-priority active watch → drives WATCHING. Null otherwise. */
  watch: NwsAlert | null
  phase: StormPhase
  isLoading: boolean
  isDemo: boolean
}

/**
 * The one place the escalation ladder is computed for the active location. Read by
 * the Home banner, the AppShell auto-enter watcher, and both elevated screens — all
 * share react-query's cache, so this is cheap to call from several components.
 */
export function useStormEscalation(): StormEscalation {
  const saved = useLocationStore((s) => s.saved)
  const { search } = useLocation()
  const alertsQ = useAlertsQuery(saved)
  const demo = demoKindFromSearch(search)

  const alerts = useMemo<NwsAlert[]>(() => {
    const live = alertsQ.data ?? []
    if (demo && saved) return [makeDemoAlert(demo, saved.lat, saved.lon), ...live]
    return live
  }, [demo, saved, alertsQ.data])

  const warning = useMemo(() => pickActiveWarning(alerts), [alerts])
  const watch = useMemo(() => pickActiveWatch(alerts), [alerts])
  const phase: StormPhase = warning ? 'storm' : watch ? 'watching' : 'quiet'

  return {
    saved,
    alerts,
    warning,
    watch,
    phase,
    isLoading: alertsQ.isLoading && !demo,
    isDemo: !!demo,
  }
}
